// @ts-strict-ignore
import { useApolloClient, useMutation, useQuery } from '@apollo/client';
import { chunk, isEmpty, isNil, keyBy, merge } from 'lodash';
import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useDebouncedCallback } from 'use-debounce';
import { Mutations, Queries } from '@shared/api';
import { CardChannelContext } from '@shared/components/blocks/contexts/channel';
import { CardFormContext } from '@shared/components/blocks/contexts/form';
import { LinkedCard } from '@shared/components/blocks/lib/cards/index';
import { CardFieldItem } from '@shared/components/blocks/types';
import { extractCardDefinitionDefaultValues } from '@shared/components/blocks/utils';
import { useCardDefinition } from '@shared/scenes/cards/hooks';
import { RelatedCardComponent } from '@shared/scenes/workflows/config';
import { useGetChannelEventHandlers } from '@shared/scenes/workflows/hooks';
import { hasRelatedCardFilter } from '@shared/scenes/workflows/utils';
import { useChannelsLite } from '@shared/util/hooks';
import { i18n } from '@shared/util/i18n';
import { useNavigator } from '@shared/util/navigation';
import { MBStorage } from '@shared/util/storage';
import { getTimezone } from '@shared/util/timezone';
import { prepareIdInput } from '@shared/util/workflow';

export function joinRelatedCardsAndCardItems(
  relatedCards: MBAPI.Card_relatedCards[],
  cardItems: CardFieldItem[],
): LinkedCard[] {
  if (isEmpty(relatedCards) || !cardItems) {
    return [];
  }

  const cardItemsById = keyBy(cardItems, 'id');
  return relatedCards
    .filter(relatedCard => !isNil(cardItemsById[relatedCard.id]))
    .map(relatedCard => ({
      cardId: relatedCard.id,
      name: relatedCard.name,
      quantity: cardItemsById[relatedCard.id]?.quantity,
    }));
}

const MAX_SUMMARY_CARDS_QUERY_COUNT = 50;

export function useGetSummaryCards(
  ids: string[],
): MBAPI.ListSummaryCardsQuery_listSummaryCards_cards[] {
  const prevLen = useRef(0);
  const apolloClient = useApolloClient();
  const [cards, setCards] = useState([]);

  useEffect(() => {
    if (!apolloClient || ids.length === prevLen.current) {
      return;
    }

    prevLen.current = ids.length;

    const loadPages = async () => {
      const promises = [];
      const chunkedIds = chunk(ids, MAX_SUMMARY_CARDS_QUERY_COUNT);

      chunkedIds.forEach(currentChunk => {
        const promise = apolloClient.query<
          MBAPI.GetSummaryCardsQuery,
          MBAPI.GetSummaryCardsQueryVariables
        >({
          query: Queries.card.getSummaryCards,
          variables: {
            input: {
              ids: currentChunk,
            },
          },
          fetchPolicy: 'network-only',
        });
        promises.push(promise);
      });

      return Promise.all(promises);
    };

    loadPages().then(allChunks => {
      if (!isEmpty(allChunks)) {
        setCards(allChunks.reduce((acc, c) => acc.concat(c.data.getSummaryCards), []));
      }
    });
  }, [ids, apolloClient]);

  return cards;
}

export function useSearchCards(
  fromChannelId?: number,
  relatedCardComponent?: RelatedCardComponent,
): {
  keyword: string;
  onSearch(keyword: string): void;
  onAdd(card: MBAPI.Card): void;
  searchResults: LinkedCard[];
  reset(): void;
  loading: boolean;
  hasMore: boolean;
} {
  const [keyword, _setKeyword] = useState<string>('');
  const [setKeyword] = useDebouncedCallback(_setKeyword, 100);
  const [searchResults, setSearchResults] = useState<LinkedCard[]>([]);
  const [recentlyAddedCard, setRecentlyAddedCard] = useState<LinkedCard>();
  const query = hasRelatedCardFilter(relatedCardComponent)
    ? {
        nameQuery: keyword,
        workflowTemplateId: relatedCardComponent.workflowTemplateId,
      }
    : {
        nameQuery: keyword,
        channelId: fromChannelId,
      };

  const {
    data: listCardsSummary,
    loading: listLoading,
    refetch,
  } = useQuery<MBAPI.ListSummaryCardsQuery, MBAPI.ListSummaryCardsQueryVariables>(
    Queries.card.listSummaryCards,
    {
      variables: {
        input: query,
      },
      skip: !isEmpty(fromChannelId),
      fetchPolicy: 'network-only',
    },
  );

  useEffect(() => {
    refetch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const cardResults =
      listCardsSummary?.listSummaryCards?.cards.map<LinkedCard>(s => ({
        cardId: s.id,
        name: s.name,
      })) ?? [];
    const missingRecentlyAddedCard =
      !isNil(recentlyAddedCard) &&
      isNil(
        cardResults.find(value => {
          return value.cardId === recentlyAddedCard.cardId;
        }),
      );

    if (missingRecentlyAddedCard) {
      cardResults.push(recentlyAddedCard);
    }

    setSearchResults(cardResults);
  }, [listCardsSummary?.listSummaryCards?.cards, recentlyAddedCard]);

  const reset = useCallback(() => _setKeyword(''), []);

  const onAdd = useCallback(
    (card: MBAPI.Card) => {
      setRecentlyAddedCard({ cardId: card.id, name: card.fields.name });
      refetch();
    },
    [refetch],
  );

  return {
    keyword,
    reset,
    onSearch: setKeyword,
    onAdd,
    searchResults,
    loading: listLoading,
    hasMore: listCardsSummary?.listSummaryCards?.hasMore,
  };
}

export function useChannelRelations(channelId: number) {
  const { data, loading, refetch } = useQuery<
    MBAPI.RelatedChannelAssociationsQuery,
    MBAPI.RelatedChannelAssociationsQueryVariables
  >(Queries.channel.getRelatedChannelAssociations, {
    variables: { id: channelId },
    skip: isNil(channelId),
    fetchPolicy: 'network-only',
  });
  return { data, loading, refetch };
}

export function makeKeyForManualUnlink(
  sourceChannelId: number,
  targetChannelId: number,
  fieldName: string,
): string {
  return `RelatedChannels.unlink.${sourceChannelId}:${targetChannelId}.${fieldName}`;
}

interface UseRelatedCardsResponse {
  ids: number[];
  remove(id: number): Promise<void>;
  add(id: number): Promise<void>;
}

export function useRelatedChannels(
  fieldName: string,
  overrideChannelId?: number,
): UseRelatedCardsResponse {
  const channel = useContext(CardChannelContext);
  const channelId = isNil(overrideChannelId) ? channel?.id : overrideChannelId;
  const { data, loading, refetch } = useChannelRelations(channelId);

  const [updateRelatedChannelAssociations] = useMutation<
    MBAPI.UpdateChannelV2Mutation,
    MBAPI.UpdateChannelV2MutationVariables
  >(Mutations.channel.updateChannelV2);

  const ids = useMemo<number[]>(() => {
    if (loading) {
      return null;
    }
    if (data?.getChannel) {
      return data.getChannel.relatedChannelAssociations
        .filter(rel => rel.field === fieldName)
        .map(rel => rel.relatedChannelId);
    }
    return [];
  }, [data, loading, fieldName]);

  const update = useCallback(
    async (relatedChannelAssociations: MBAPI.UpdateRelatedChannelAssociation[]) => {
      await updateRelatedChannelAssociations({
        variables: {
          input: {
            id: channelId,
            relatedChannelAssociations,
          },
        },
      });
      await refetch();
    },
    [channelId, refetch, updateRelatedChannelAssociations],
  );

  const remove = useCallback(
    async (relatedChannelId: number) => {
      const latest = await refetch();
      await MBStorage.set(makeKeyForManualUnlink(channelId, relatedChannelId, fieldName), 'manual');
      await update(
        latest.data.getChannel.relatedChannelAssociations.filter(
          rel => !(rel.field === fieldName && rel.relatedChannelId === relatedChannelId),
        ),
      );
    },
    [channelId, fieldName, refetch, update],
  );

  const add = useCallback(
    async (relatedChannelId: number) => {
      const latest = await refetch();
      await update([
        ...latest.data.getChannel.relatedChannelAssociations,
        { relatedChannelId, field: fieldName },
      ]);
    },
    [fieldName, refetch, update],
  );

  return useMemo(() => ({ ids, remove, add }), [ids, remove, add]);
}

export function useCardChannel(channelId: number): MBAPI.ChannelListItem {
  const { loading, channels } = useChannelsLite();
  return useMemo<MBAPI.ChannelListItem>(() => {
    if (!loading) {
      return channels.find(c => c.id === channelId);
    }
    return null;
  }, [channelId, loading, channels]);
}

function getRelationFieldForId(id: string): [CardFieldItem] {
  return [{ id }];
}

export function useOpenNewLinkedCard({
  cardId,
  externalChannelId,
}: {
  cardId: string;
  externalChannelId: number;
}) {
  const navigator = useNavigator();
  const channel = useContext(CardChannelContext);

  const { data } = useQuery<
    MBAPI.RelatedChannelAssociationsQuery,
    MBAPI.RelatedChannelAssociationsQueryVariables
  >(Queries.channel.getRelatedChannelAssociations, {
    variables: { id: externalChannelId },
  });
  const redirectParams = useMemo(() => {
    if (!data) {
      return null;
    }
    const channelSlug = data.getChannel.slug;
    const thisRelation = data.getChannel.relatedChannelAssociations.find(
      ({ relatedChannelId }) => relatedChannelId === channel.id,
    );
    return {
      fieldOverrides: {
        [thisRelation?.field]: getRelationFieldForId(cardId),
      },
      channelSlug,
    };
  }, [data, cardId, channel]);

  return useCallback(
    (fieldOverrides: Record<string, string> = {}) => {
      if (!redirectParams) {
        navigator.navigate('CardDetailsNoAccess', { channelSlug: channel.slug });
      } else {
        const params = merge({ ...redirectParams }, { fieldOverrides });
        navigator.navigate('CardCreate', params);
      }
    },
    [navigator, redirectParams, channel],
  );
}

export function useOpenLinkedCard(): (linkedCard: LinkedCard) => Promise<void> {
  const apolloClient = useApolloClient();
  const navigator = useNavigator();
  const currentChannel = useContext(CardChannelContext);
  const getChannelEventHandlers = useGetChannelEventHandlers();
  return useCallback(
    async (linkedCard: LinkedCard) => {
      try {
        const card: MBAPI.Card = await apolloClient
          .query<MBAPI.CardDetailsQuery, MBAPI.CardDetailsQueryVariables>({
            query: Queries.card.details,
            variables: { id: linkedCard.cardId },
          })
          .then(res => res.data?.card);

        if (!card) {
          return;
        }

        const channel = await apolloClient
          .query<MBAPI.ChannelQuery, MBAPI.ChannelQueryVariables>({
            query: Queries.channel.getChannel,
            variables: { id: card.channelId },
          })
          .then(res => res.data?.channel);

        if (!channel) {
          return;
        }
        const { onOpenCard } = await getChannelEventHandlers(card.id);
        onOpenCard(card);
      } catch (error) {
        if (error.message === 'Forbidden resource') {
          navigator.navigate('CardDetailsNoAccess', { channelSlug: currentChannel.slug });
        }
      }
    },
    [apolloClient, getChannelEventHandlers, navigator, currentChannel.slug],
  );
}

export function useQuickCreateCard(
  channel: MBAPI.ChannelQuery_channel,
): (name: string) => Promise<MBAPI.Card> {
  const [createCard] = useMutation<MBAPI.CreateCardMutation, MBAPI.CreateCardMutationVariables>(
    Mutations.cards.create,
  );

  const { cardDefinition } = useCardDefinition(channel?.id, channel?.workflowTemplateId);
  const defaultFields = useMemo(
    () => extractCardDefinitionDefaultValues(cardDefinition?.definition),
    [cardDefinition],
  );

  return useCallback(
    async (name: string) => {
      try {
        const res = await createCard({
          variables: {
            input: {
              channelId: channel.id,
              cardDefinitionId: cardDefinition?.id,
              workflowTemplateId: prepareIdInput(channel?.workflowTemplateId),
              fields: { ...defaultFields, name },
              kanbanSortKey: Date.now().toString(),
              cardReminders: null,
              entityRecurrence: null,
              assigneeIds: [],
              urgent: false,
              timezone: getTimezone(),
            },
          },
        });
        return res.data?.createCard;
      } catch {
        throw Error(i18n('card.blocks.cardsInput.quickCreateError'));
      }
    },
    [createCard, channel, cardDefinition, defaultFields],
  );
}

export function useCardItems(cardFieldNames: string[]): CardFieldItem[] {
  const { values } = useContext(CardFormContext);
  const value = useMemo<(CardFieldItem | string)[]>(() => {
    return cardFieldNames.map(cardFieldName => values[cardFieldName] ?? []).flat();
  }, [values, cardFieldNames]);

  return useMemo(
    () =>
      value.map(cardItemOrId =>
        typeof cardItemOrId === 'string' ? { id: cardItemOrId } : cardItemOrId,
      ),
    [value],
  );
}
